import { useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Card, CardBody, CardHeader, Spinner } from 'grommet';
import { Upload } from 'grommet-icons';
import FileInput from '../../components/common/FileInput';
import { Text } from '/src/components';
import { toastMessages } from '/src/lib/toast';

interface FileImporterCardProps {
  title: string;
  description: string;
  onFileSelect: (file: File) => void;
  onUpload: () => Promise<void>;
  isUploading: boolean;
  selectedFile: File | null;
  validationErrors: Record<string, string[]>;
  successMessage: string | null;
}

export const FileImporterCard = ({
  title,
  description,
  onFileSelect,
  onUpload,
  isUploading,
  selectedFile,
  validationErrors,
  successMessage,
}: FileImporterCardProps) => {
  const [isErrorsExpanded, setIsErrorsExpanded] = useState(false);

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      toast.error(toastMessages.fileValidation.invalidExcelFile);
      return;
    }
    onFileSelect(file);
    setIsErrorsExpanded(false);
  };

  return (
    <Card background="white" elevation="none" round="xsmall">
      <CardHeader
        pad={{ horizontal: 'medium', vertical: 'medium' }}
        border={{ side: 'bottom', color: 'light-4', size: '1px' }}
      >
        <Text size="large" weight="normal">
          {title}
        </Text>
      </CardHeader>
      <CardBody pad="medium">
        <Box gap="medium">
          <Text>{description}</Text>

          {successMessage && (
            <Box background="#4CAF50" pad="medium" round="small">
              <Text color="white">{successMessage}</Text>
            </Box>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <Box background="#C04B4B" pad="medium" round="small">
              {validationErrors.general && <Text color="white">{validationErrors.general[0]}</Text>}

              {Object.keys(validationErrors).filter((key) => key !== 'general').length > 0 && (
                <>
                  <Text weight="bold" margin={{ bottom: 'small' }} color="white">
                    Found errors in {Object.keys(validationErrors).filter((key) => key !== 'general').length} rows:
                  </Text>
                  <Box gap="xsmall">
                    {Object.entries(validationErrors)
                      .filter(([key]) => key !== 'general')
                      .slice(0, isErrorsExpanded ? undefined : 5)
                      .map(([row, errors]) => (
                        <Box key={row} gap="xsmall">
                          <Text color="white">Row {row}:</Text>
                          {errors.map((error, index) => (
                            <Text key={index} margin={{ left: 'medium' }} color="white">
                              {error}
                            </Text>
                          ))}
                        </Box>
                      ))}
                  </Box>
                  {Object.keys(validationErrors).filter((key) => key !== 'general').length > 5 && (
                    <Button
                      plain
                      onClick={() => setIsErrorsExpanded(!isErrorsExpanded)}
                      label={isErrorsExpanded ? 'Show less' : 'Show more'}
                      margin={{ top: 'medium' }}
                      color="white"
                      style={{ color: 'white' }}
                    />
                  )}
                </>
              )}
            </Box>
          )}

          <Box direction="row" gap="small" align="center">
            <FileInput
              onFileSelect={handleFileSelect}
              isUploading={isUploading}
              accept=".xlsx"
              disabled={isUploading}
              selectedFile={selectedFile}
              id={title.toLowerCase().replace(/\s+/g, '-')}
            />
            <Button
              primary
              icon={<Upload />}
              label="Upload"
              onClick={onUpload}
              disabled={!selectedFile || isUploading}
              style={{ fontWeight: 'normal' }}
            />
            {isUploading && <Spinner />}
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};
